import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Progress } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Loader2, RotateCcw, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useProposalProgressContext } from '../../contexts/ProposalProgressContext';
import { useProjectFiles } from '../../hooks/useProjectFiles';
import { useProposalProgressPreferences } from '../../hooks/useProposalProgressPreferences';
import { proposalProjectApiConnect } from '../../lib/api/proposalProjectApi.connect';
import { ProposalProgressSheet } from './ProposalProgressSheet';
const AUTO_HIDE_DELAY_MS = 5000;
/**
 * Compact document-analysis progress strip rendered in the proposal page header.
 *
 * Renders null outside of `/proposal/:id/*` routes (no provider in scope) and
 * when there is no active batch. Reuses the shared `useProposalProgressContext`
 * snapshot, so it shares one 2s poll with the larger `BatchProcessingProgress`
 * via React Query key dedupe.
 */ export function ProposalProgressBar() {
    const { projectId, batchId, snapshot } = useProposalProgressContext();
    const { autoHide } = useProposalProgressPreferences();
    const queryClient = useQueryClient();
    // Manual dismissal is keyed to batchId so a new batch re-shows the bar.
    const [dismissedBatchId, setDismissedBatchId] = useState(null);
    const [autoHidden, setAutoHidden] = useState(false);
    const [sheetOpen, setSheetOpen] = useState(false);
    // Reset local hidden state when batchId changes.
    useEffect(()=>{
        setDismissedBatchId(null);
        setAutoHidden(false);
    }, [
        batchId
    ]);
    // Auto-hide on completion, if the user pref allows. The pref also gates
    // the hidden state: flipping autoHide off after the bar has already faded
    // out restores visibility (the user explicitly asked to see it again).
    useEffect(()=>{
        if (!snapshot?.isFullyComplete || !autoHide) return;
        const t = setTimeout(()=>setAutoHidden(true), AUTO_HIDE_DELAY_MS);
        return ()=>clearTimeout(t);
    }, [
        snapshot?.isFullyComplete,
        autoHide
    ]);
    useEffect(()=>{
        if (!autoHide) setAutoHidden(false);
    }, [
        autoHide
    ]);
    // The files list is only needed when the user clicks Reprocess (failed
    // state). Skipping the fetch on every proposal page mount keeps the bar
    // cheap — it's mounted on every `/proposal/:id/*` route by design.
    const filesQuery = useProjectFiles(snapshot?.isFailed ? projectId : null, undefined);
    const retry = useMutation({
        mutationFn: async ()=>{
            if (!projectId) throw new Error('Missing projectId');
            const attachmentIds = (filesQuery.data?.files ?? []).map((f)=>f.attachmentId).filter(Boolean);
            if (attachmentIds.length === 0) {
                throw new Error('No documents available to reprocess');
            }
            // `force: true` is a REST-only knob: ProcessBatchRequest in
            // proto/mono/projectdata/v1/projectdata.proto has no equivalent
            // field, so the Connect path falls back to zou's default reprocess
            // behaviour. This matches the existing asymmetry in
            // ProjectFilesSection (which calls processBatch the same way) —
            // adding a proto field is a follow-up, not in scope here.
            return proposalProjectApiConnect.processBatch(projectId, attachmentIds);
        },
        onSuccess: ()=>{
            if (projectId) {
                queryClient.invalidateQueries({
                    queryKey: [
                        'proposal-project',
                        projectId
                    ]
                });
            }
            toast.success('Reprocessing started');
        },
        onError: (err)=>{
            const message = err instanceof Error ? err.message : 'Reprocess failed';
            toast.error(message);
        }
    });
    const handleDismiss = useCallback(()=>{
        if (batchId) setDismissedBatchId(batchId);
    }, [
        batchId
    ]);
    // No project/batch means the bar AND the sheet have nothing to show.
    if (!projectId || !batchId || !snapshot) return null;
    // Bar-only gates: dismissal + post-completion auto-hide. The sheet is
    // rendered outside these so it doesn't yank itself closed mid-view if
    // the auto-hide timer fires while the user is reading it.
    //
    // The `autoHide` clause is defensive — today autoHidden can only become
    // true while autoHide is true, and the effect at line ~50 resets it on
    // flip-off. If a future change breaks either invariant, this clause
    // still keeps the bar visible whenever the user has the pref off.
    const barHidden = dismissedBatchId === batchId || snapshot.isFullyComplete && autoHidden && autoHide;
    const isFailed = snapshot.isFailed;
    const isComplete = snapshot.isFullyComplete;
    const isProcessing = snapshot.isActive;
    // For pre-track "completed" the percentage is 100; cross-doc analysis
    // contributes via projectAnalysisPercentage when present.
    const displayPct = (()=>{
        if (isComplete) return 100;
        if (snapshot.status === 'completed' && typeof snapshot.projectAnalysisPercentage === 'number') {
            return snapshot.projectAnalysisPercentage;
        }
        return snapshot.percentage;
    })();
    const label = (()=>{
        if (isFailed) return 'Analysis failed';
        if (isComplete && snapshot.projectAnalysisStatus === 'not_required') return 'Award document ready for extraction';
        if (isComplete) return 'Analysis complete';
        if (snapshot.status === 'completed') {
            return snapshot.projectAnalysisStatus === 'processing' ? 'Cross-document analysis…' : 'Finalizing analysis…';
        }
        if (snapshot.totalDocuments > 0) {
            return `Analyzing ${Math.min(snapshot.documentsProcessed + 1, snapshot.totalDocuments)} of ${snapshot.totalDocuments}`;
        }
        return snapshot.currentStep || 'Analyzing documents';
    })();
    const tone = isFailed ? 'border-destructive/40 bg-destructive/5 text-destructive' : isComplete ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-blue-200 bg-blue-50 text-blue-800';
    const indicatorClass = isFailed ? '[&>div]:bg-destructive' : isComplete ? '[&>div]:bg-emerald-500' : '[&>div]:bg-blue-500';
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            barHidden ? null : /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.proposalProgress.bar,
                className: `flex items-center gap-3 border-b px-4 py-1.5 text-xs ${tone}`,
                children: [
                    /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        onClick: ()=>setSheetOpen(true),
                        "aria-label": "View document analysis details",
                        "data-testid": Testids.proposalProgress.expand,
                        className: "flex min-w-0 flex-1 items-center gap-3 rounded text-left transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current/40",
                        children: [
                            isFailed ? /*#__PURE__*/ _jsx(AlertCircle, {
                                className: "h-3.5 w-3.5 shrink-0",
                                "aria-hidden": true
                            }) : isComplete ? /*#__PURE__*/ _jsx(CheckCircle2, {
                                className: "h-3.5 w-3.5 shrink-0",
                                "aria-hidden": true
                            }) : /*#__PURE__*/ _jsx(Loader2, {
                                className: "h-3.5 w-3.5 shrink-0 animate-spin",
                                "aria-hidden": true
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                "data-testid": Testids.proposalProgress.label,
                                className: "min-w-0 flex-shrink-0 font-medium",
                                children: label
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex-1",
                                children: /*#__PURE__*/ _jsx(Progress, {
                                    value: isFailed ? 100 : displayPct,
                                    className: `h-1 bg-white/60 ${indicatorClass}`
                                })
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "tabular-nums text-[11px] opacity-75",
                                children: isFailed ? '!' : `${Math.round(displayPct)}%`
                            }),
                            isProcessing && snapshot.currentStep ? /*#__PURE__*/ _jsx("span", {
                                className: "hidden max-w-[28ch] truncate text-[11px] opacity-75 md:inline",
                                title: snapshot.currentStep,
                                children: snapshot.currentStep
                            }) : null
                        ]
                    }),
                    isFailed ? /*#__PURE__*/ _jsxs(Button, {
                        variant: "ghost",
                        size: "sm",
                        className: "h-6 gap-1 px-2 text-destructive hover:bg-destructive/10",
                        onClick: ()=>retry.mutate(),
                        // Disabled while the files list is still loading — without this
                        // gate, an instant click after the bar flips to failed races
                        // the just-enabled query and surfaces a misleading
                        // "No documents available to reprocess" toast.
                        disabled: retry.isPending || filesQuery.isLoading,
                        "data-testid": Testids.proposalProgress.retry,
                        children: [
                            retry.isPending || filesQuery.isLoading ? /*#__PURE__*/ _jsx(Loader2, {
                                className: "h-3 w-3 animate-spin"
                            }) : /*#__PURE__*/ _jsx(RotateCcw, {
                                className: "h-3 w-3"
                            }),
                            "Reprocess"
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "aria-label": "Dismiss progress bar",
                        onClick: handleDismiss,
                        className: "rounded p-0.5 opacity-60 transition-opacity hover:opacity-100",
                        "data-testid": Testids.proposalProgress.dismiss,
                        children: /*#__PURE__*/ _jsx(X, {
                            className: "h-3 w-3"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ProposalProgressSheet, {
                projectId: projectId,
                batchId: batchId,
                open: sheetOpen,
                onOpenChange: setSheetOpen
            })
        ]
    });
}
